<script setup>
import { reactive, computed, ref, getCurrentInstance, watch } from 'vue';
import { useForm, router } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';
import { useOrderStatus } from '@/composables/useOrderStatus.js';
import ContentReviewSection from '@/components/admin/orders/ContentReviewSection.vue';

import PublicationSection from '@/components/admin/orders/PublicationSection.vue';
import PaymentInformationSection from '@/components/payment/PaymentInformationSection.vue';
import CompanySelectionSection from '@/components/invoice/CompanySelectionSection.vue';
import BankAccountSelectionSection from '@/components/invoice/BankAccountSelectionSection.vue';
import CompanyFormModal from '@/components/admin/CompanyFormModal.vue';
import BankAccountFormModal from '@/components/admin/BankAccountFormModal.vue';
import InvoiceView from '@/components/orders/InvoiceView.vue';
import RecipientContactBlock from '@/components/admin/orders/RecipientContactBlock.vue';
import PaymentRequestMismatchModal from '@/components/modals/PaymentRequestMismatchModal.vue';

defineOptions({
    layout: AdminMainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();
const { canCancelOrder, getStatusLabel, getStatusSeverity, getCancellationTooltip } = useOrderStatus();

const goToEditOrder = () => router.visit(proxy.route ? proxy.route('orders.edit', props.order.id) : `/admin/orders/${props.order.id}/edit`);

const getOrderStatusLabel = (status) => {
    if (status === 'SUBMITTED') return $t('Approved');
    return getStatusLabel(status, $t);
};

const props = defineProps({
    order: {
        type: Object,
        required: true
    },
    itemsWithContent: {
        type: Array,
        default: () => []
    },
    allConfirmed: {
        type: Boolean,
        default: false
    },
    hasContent: {
        type: Boolean,
        default: false
    },
    allSent: {
        type: Boolean,
        default: false
    },
    hasConfirmedContent: {
        type: Boolean,
        default: false
    },
    paymentRequest: {
        type: Object,
        default: () => null
    },
    companies: {
        type: Array,
        default: () => []
    },
    bankAccounts: {
        type: Array,
        default: () => []
    },
    invoice: {
        type: Object,
        default: () => null
    },
    invoiceSettings: {
        type: Object,
        default: () => ({})
    },
    hasAutoContent: {
        type: Boolean,
        default: false
    },
    allClientApproved: {
        type: Boolean,
        default: true
    }
});

const confirmForm = useForm({
    payment_date: props.order.payment_date ? new Date(props.order.payment_date + 'T12:00:00') : null,
    deferred_payment: false,
});

watch(() => confirmForm.deferred_payment, (newValue) => {
    if (newValue) {
        confirmForm.payment_date = null;
    }
});

const cancelForm = useForm({});
const cancelReason = ref('');
const deleteOrderDialog = ref(false);

const showPaymentLinkDialog = ref(false);
const paymentRequestForm = useForm({
    payment_link: '',
    company_profile_id: null,
    bank_account_id: null,
});
const selectedCompany = ref(null);
const selectedBankAccount = ref(null);
const paymentLinkComponentRef = ref(null);

const paymentLinkOption = ref('none');
const customPaymentLink = ref('');

const finalPaymentLink = computed(() => {
    switch (paymentLinkOption.value) {
        case 'bank_account':
            return selectedBankAccount.value?.payment_link || '';
        case 'custom':
            return customPaymentLink.value || '';
        case 'none':
        default:
            return '';
    }
});

const canSendPaymentRequest = computed(() => {
    if (props.order.deferred_payment) return false;

    if (props.order.invoice) return false;

    if (props.order.payment_date) return false;

    if (!selectedCompany.value || !selectedBankAccount.value || paymentRequestForm.processing) {
        return false;
    }

    if (paymentLinkComponentRef.value && paymentLinkComponentRef.value.isPaymentLinkValid !== undefined) {
        return paymentLinkComponentRef.value.isPaymentLinkValid;
    }

    return true;
});

const canGenerateInvoice = computed(() => {
    if (props.order.invoice) return false;

    if (props.order.payment_date) return false;

    if (props.order.deferred_payment) return false;

    return true;
});

const isInvoiceBlockedByPayment = computed(() => {
    return props.order.invoice && props.order.payment_date;
});

const hasInvoice = computed(() => {
    return !!props.order.invoice;
});

const isPaymentLinkAlreadySent = computed(() => {
    return props.invoice?.payment_link_sent === true;
});

const currentPaymentRequestData = computed(() => {
    const invoiceCompany = props.invoice?.company || (props.invoice?.company_profile_id ? props.companies.find(c => c.id === props.invoice.company_profile_id) : null);
    const invoiceBankAccount = props.invoice?.bankAccount || (props.invoice?.bank_account_id ? props.bankAccounts.find(b => b.id === props.invoice.bank_account_id) : null);

    const data = {
        company_profile_id: selectedCompany.value?.id || props.invoice?.company_profile_id || props.paymentRequest?.company_profile_id,
        bank_account_id: selectedBankAccount.value?.id || props.invoice?.bank_account_id || props.paymentRequest?.bank_account_id,
        payment_link: finalPaymentLink.value || props.invoice?.payment_link || props.paymentRequest?.payment_link,
        company: selectedCompany.value || invoiceCompany || props.paymentRequest?.company,
        bankAccount: selectedBankAccount.value || invoiceBankAccount || props.paymentRequest?.bankAccount
    };

    return data;
});

const showCompanyModal = ref(false);
const showBankAccountModal = ref(false);
const editingCompany = ref(null);
const editingBankAccount = ref(null);
const showGenerateInvoiceDialog = ref(false);
const showPaymentMismatchDialog = ref(false);
const pendingPaymentCallback = ref(null);
const invoiceForm = useForm({
    send_to_client: true,
    selected: []
});

const initializePaymentRequestData = () => {
    if (props.paymentRequest) {
        if (props.companies.length > 0 && props.paymentRequest.company_profile_id) {
            selectedCompany.value = props.companies.find(c => c.id === props.paymentRequest.company_profile_id);
            paymentRequestForm.company_profile_id = props.paymentRequest.company_profile_id;
        }

        if (props.bankAccounts.length > 0 && props.paymentRequest.bank_account_id) {
            selectedBankAccount.value = props.bankAccounts.find(b => b.id === props.paymentRequest.bank_account_id);
            paymentRequestForm.bank_account_id = props.paymentRequest.bank_account_id;
        }

        if (props.paymentRequest.payment_link) {
            paymentLinkOption.value = 'custom';
            customPaymentLink.value = props.paymentRequest.payment_link;
        } else if (selectedBankAccount.value?.payment_link) {
            paymentLinkOption.value = 'bank_account';
        } else {
            paymentLinkOption.value = 'none';
        }

        paymentRequestForm.payment_link = props.paymentRequest.payment_link || '';
    }
    else if (props.invoice) {
        if (props.companies.length > 0 && props.invoice.company_profile_id) {
            selectedCompany.value = props.companies.find(c => c.id === props.invoice.company_profile_id);
            paymentRequestForm.company_profile_id = props.invoice.company_profile_id;
        }

        if (props.bankAccounts.length > 0 && props.invoice.bank_account_id) {
            selectedBankAccount.value = props.bankAccounts.find(b => b.id === props.invoice.bank_account_id);
            paymentRequestForm.bank_account_id = props.invoice.bank_account_id;
        }

        if (props.invoice.payment_link) {
            if (selectedBankAccount.value?.payment_link === props.invoice.payment_link) {
                paymentLinkOption.value = 'bank_account';
                customPaymentLink.value = '';
            } else {
                paymentLinkOption.value = 'custom';
                customPaymentLink.value = props.invoice.payment_link;
            }
            paymentRequestForm.payment_link = props.invoice.payment_link;
        } else if (selectedBankAccount.value?.payment_link) {
            paymentLinkOption.value = 'bank_account';
            customPaymentLink.value = '';
        } else {
            paymentLinkOption.value = 'none';
            customPaymentLink.value = '';
        }
    } else {
        if (selectedBankAccount.value?.payment_link) {
            paymentLinkOption.value = 'bank_account';
            customPaymentLink.value = '';
        } else {
            paymentLinkOption.value = 'none';
            customPaymentLink.value = '';
        }
    }
};

watch([() => props.companies, () => props.bankAccounts, () => props.paymentRequest, () => props.invoice], () => {
    initializePaymentRequestData();
}, { deep: true, immediate: true });

watch(finalPaymentLink, (newLink) => {
    paymentRequestForm.payment_link = newLink;
});

const isPaymentConfirmed = computed(() => {
    const { payment_date, deferred_payment, status } = props.order;

    if (payment_date || deferred_payment) {
        return {
            type: 'success',
            blocked: true
        };
    }

    if (!payment_date && !deferred_payment && !props.order.invoice && (status === 'CANCELED' ||
        status === 'SUBMITTED' ||
        status === 'PROCESSING' ||
        status === 'INVOICE' ||
        status === 'COMPLETED')) {
        return {
            type: 'warn',
            blocked: true
        };
    }

    return null;
});

const canConfirmPayment = computed(() => {
    if (isPaymentConfirmed.value) return false;
    return confirmForm.deferred_payment || confirmForm.payment_date;
});

const isContentReviewEnabled = computed(() => {
    return isPaymentConfirmed.value !== null;
});

const isPublicationEnabled = computed(() => {
    return isPaymentConfirmed.value !== null && props.allConfirmed && (!props.hasAutoContent || props.allClientApproved);
});

const handleRefreshNeeded = () => {
    router.visit(
        proxy.route ? proxy.route('orders.administration', props.order.id) : `/admin/orders/${props.order.id}/order-administration`,
        { preserveScroll: true }
    );
};

const handlePublicationConfirmed = () => {
    router.visit(
        proxy.route ? proxy.route('orders.administration', props.order.id) : `/admin/orders/${props.order.id}/order-administration`,
        {
            preserveScroll: true,
            onSuccess: () => {
                if (props.allSent) {
                    router.visit(
                        proxy.route ? proxy.route('orders.index') : '/admin/orders',
                        {
                            onStart: () => showLoading($t('Loading...')),
                            onFinish: () => hideLoading()
                        }
                    );
                }
            }
        }
    );
};

const openPaymentLinkDialog = () => {
    if (props.paymentRequest) {
        return;
    }

    if (!props.invoice) {
        paymentRequestForm.reset();
        paymentRequestForm.payment_link = '';
        paymentRequestForm.company_profile_id = null;
        paymentRequestForm.bank_account_id = null;
        selectedCompany.value = null;
        selectedBankAccount.value = null;

        paymentLinkOption.value = 'none';
        customPaymentLink.value = '';
    } else {
        paymentRequestForm.company_profile_id = selectedCompany.value?.id;
        paymentRequestForm.bank_account_id = selectedBankAccount.value?.id;
        paymentRequestForm.payment_link = finalPaymentLink.value || '';
    }

    showPaymentLinkDialog.value = true;
};

const closePaymentLinkDialog = () => {
    showPaymentLinkDialog.value = false;
    paymentRequestForm.reset();
};

const sendPaymentLinkRequest = () => {
    if (paymentLinkComponentRef.value && typeof paymentLinkComponentRef.value.validateForSending === 'function' && !paymentLinkComponentRef.value.validateForSending()) {
        return;
    }

    const referenceData = props.paymentRequest || (props.invoice ? {
        company_profile_id: props.invoice.company_profile_id,
        bank_account_id: props.invoice.bank_account_id,
        payment_link: props.invoice.payment_link
    } : null);

    if (referenceData) {
        const companyDiverges = selectedCompany.value?.id !== referenceData.company_profile_id;
        const bankAccountDiverges = selectedBankAccount.value?.id !== referenceData.bank_account_id;
        const paymentLinkDiverges = finalPaymentLink.value !== referenceData.payment_link;

        if (companyDiverges || bankAccountDiverges || paymentLinkDiverges) {
            pendingPaymentCallback.value = performPaymentLinkRequest;
            showPaymentMismatchDialog.value = true;
            return;
        }
    }

    performPaymentLinkRequest();
};

const performPaymentLinkRequest = () => {
    paymentRequestForm.company_profile_id = selectedCompany.value?.id || null;
    paymentRequestForm.bank_account_id = selectedBankAccount.value?.id || null;

    let paymentLinkToSend = '';
    switch (paymentLinkOption.value) {
        case 'bank_account':
            paymentLinkToSend = selectedBankAccount.value?.payment_link || '';
            break;
        case 'custom':
            paymentLinkToSend = customPaymentLink.value || '';
            break;
        case 'none':
        default:
            paymentLinkToSend = '';
            break;
    }

    paymentRequestForm.payment_link = paymentLinkToSend;

    paymentRequestForm.post(
        proxy.route ? proxy.route('orders.payment-request.send', props.order.id) : `/admin/orders/${props.order.id}/payment-request/send`,
        {
            preserveScroll: true,
            onStart: () => showLoading($t('Sending payment request...')),
            onFinish: () => hideLoading(),
            onSuccess: () => {
                closePaymentLinkDialog();
                if (props.order.invoice && (selectedCompany.value || selectedBankAccount.value || finalPaymentLink.value)) {
                }
                handleRefreshNeeded();
            }
        }
    );
};

const proceedWithPaymentMismatch = () => {
    showPaymentMismatchDialog.value = false;
    if (pendingPaymentCallback.value) {
        pendingPaymentCallback.value();
        pendingPaymentCallback.value = null;
    }
};

const cancelPaymentMismatch = () => {
    showPaymentMismatchDialog.value = false;
    pendingPaymentCallback.value = null;
};

const openCompanyModal = () => {
    editingCompany.value = selectedCompany.value;
    showCompanyModal.value = true;
};

const createNewCompany = () => {
    editingCompany.value = null;
    showCompanyModal.value = true;
};

const openBankAccountModal = () => {
    editingBankAccount.value = selectedBankAccount.value;
    showBankAccountModal.value = true;
};

const createNewBankAccount = () => {
    editingBankAccount.value = null;
    showBankAccountModal.value = true;
};

const handleCompanySaved = () => {
    showCompanyModal.value = false;
    handleRefreshNeeded();
};

const handleBankAccountSaved = () => {
    showBankAccountModal.value = false;
    handleRefreshNeeded();
};

const openGenerateInvoiceDialog = () => {
    invoiceForm.selected = [props.order.id];
    invoiceForm.send_to_client = true;

    if (!selectedCompany.value && !selectedBankAccount.value) {
        initializePaymentRequestData();
    }

    showGenerateInvoiceDialog.value = true;
};

const closeGenerateInvoiceDialog = () => {
    showGenerateInvoiceDialog.value = false;
};

const generateInvoice = () => {
    const invoiceData = {
        selected: invoiceForm.selected,
        send_to_client: invoiceForm.send_to_client
    };

    if (selectedCompany.value) {
        invoiceData.company_profile_id = selectedCompany.value.id;
    }
    if (selectedBankAccount.value) {
        invoiceData.bank_account_id = selectedBankAccount.value.id;
    }
    if (finalPaymentLink.value) {
        invoiceData.payment_link = finalPaymentLink.value;
    }

    invoiceForm.transform(() => invoiceData).post(
        proxy.route ? proxy.route('invoices.store') : '/admin/invoices',
        {
            preserveScroll: true,
            onStart: () => showLoading($t('Generating invoice...')),
            onFinish: () => hideLoading(),
            onSuccess: (page) => {
                closeGenerateInvoiceDialog();
                handleRefreshNeeded();
            }
        }
    );
};

const viewInvoice = () => {
    router.visit(
        proxy.route ? proxy.route('orders.invoice.show', props.order.id) : `/admin/orders/${props.order.id}/invoice`,
        {
            onStart: () => showLoading($t('Loading invoice...')),
            onFinish: () => hideLoading()
        }
    );
};

const confirmPayment = () => {
    confirmForm.put(
        proxy.route ? proxy.route('orders.administration.update', props.order.id) : `/admin/orders/${props.order.id}/order-administration`,
        {
            preserveScroll: true,
            onStart: () => showLoading($t('Confirming payment...')),
            onFinish: () => hideLoading(),
            onSuccess: () => {
                handleRefreshNeeded();
            }
        }
    );
};

const showCancelDialog = () => {
    deleteOrderDialog.value = true;
};

const cancelOrder = () => {
    deleteOrderDialog.value = false;

    cancelForm.transform(data => ({
        ...data,
        cancellation_reason: cancelReason.value.trim() || null,
    })).put(
        proxy.route ? proxy.route('orders.cancel', props.order.id) : `/admin/orders/${props.order.id}/cancel`,
        {
            preserveScroll: true,
            onStart: () => showLoading($t('Canceling order...')),
            onFinish: () => hideLoading(),
            onSuccess: () => {
                router.visit(
                    proxy.route ? proxy.route('orders.index') : '/admin/orders',
                    {
                        onStart: () => showLoading($t('Loading...')),
                        onFinish: () => hideLoading()
                    }
                );
            }
        }
    );
};

const getPublicationRecipient = (orderItem) => {
    const itemData = props.itemsWithContent?.find(i => i.id === orderItem.id);
    return itemData?.publication_recipient || null;
};

const hasMissingRecipientEmail = computed(() => {
    if (!props.order?.order_items) return false;
    return props.order.order_items.some(item => {
        const recipient = getPublicationRecipient(item);
        if (!recipient) return false;
        return !recipient.type;
    });
});

const publicationRecipientsMap = computed(() => {
    const map = {};
    if (props.itemsWithContent) {
        props.itemsWithContent.forEach(item => {
            if (item.publication_recipient) {
                map[item.id] = item.publication_recipient;
            }
        });
    }
    return map;
});

const formatDate = (date) => {
    if (!date) return '-';
    if (typeof date === 'string') {
        if (date.includes('T') || date.includes(' ')) {
            return new Date(date).toLocaleDateString('pt-BR');
        }
        const [year, month, day] = date.split('-');
        return new Date(year, month - 1, day).toLocaleDateString('pt-BR');
    }
    return new Date(date).toLocaleDateString('pt-BR');
};

const calculateTotal = () => {
    if (!props.order.order_items || props.order.order_items.length === 0) return 0;
    const total = props.order.order_items.reduce((total, item) => {
        const itemTotal = (item.sale || 0) + (item.payment_tax || 0);
        return total + itemTotal;
    }, 0);

    return Math.ceil(total / 5) * 5;
};

const calculateSubtotal = () => {
    if (!props.order.order_items || props.order.order_items.length === 0) return 0;
    const subtotal = props.order.order_items.reduce((total, item) => {
        return total + (item.sale || 0);
    }, 0);

    return Math.ceil(subtotal / 5) * 5;
};

const calculateTotalTax = () => {
    if (!props.order.order_items || props.order.order_items.length === 0) return 0;
    const totalTax = props.order.order_items.reduce((total, item) => {
        return total + (item.payment_tax || 0);
    }, 0);

    return Math.ceil(totalTax / 5) * 5;
};

const getOrderCurrency = () => {
    if (!props.order.order_items || props.order.order_items.length === 0) return 'USD';
    return props.order.order_items[0]?.sale_coin || 'USD';
};

const formatCurrency = (value, currency = 'USD') => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
    }).format(parseFloat(value) / 100 || 0);
};

const getLanguageName = (locale) => {
    const languageMap = {
        'en': 'English',
        'pt': 'Portuguese',
        'es': 'Spanish',
        'pt-BR': 'Portuguese (Brazil)',
        'pt_BR': 'Portuguese (Brazil)',
        'en-US': 'English (US)',
        'en_US': 'English (US)',
        'es-ES': 'Spanish (Spain)',
        'es_ES': 'Spanish (Spain)'
    };
    return languageMap[locale] || locale || '-';
};

const handlePaymentLinkValueUpdate = (value) => {
    paymentRequestForm.payment_link = value || '';
    if (paymentLinkOption.value === 'custom') {
        customPaymentLink.value = value || '';
    }
};

const handlePaymentLinkOptionUpdate = (option) => {
    if (option) {
        paymentLinkOption.value = String(option);
        if (option === 'custom') {
            customPaymentLink.value = '';
        }
    }
};

const handleCustomPaymentLinkUpdate = (value) => {
    customPaymentLink.value = String(value || '');
    if (value && value.trim() !== '') {
        paymentLinkOption.value = 'custom';
    }
};

</script>

<template>
    <div class="grid pb-10">
        <!-- Header Card -->
        <Card class="mb-6">
            <template #header>
                <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between p-6 pb-0 gap-4">
                    <div>
                        <h1 class="text-2xl font-bold text-surface-900">
                            {{ $t('Order Administration') }}
                        </h1>
                        <p class="text-surface-600 mt-1">
                            {{ $t('Order') }} #{{ order.number }} - {{ order.client?.name || (order.client?.first_name + ' ' + order.client?.last_name) }}
                        </p>
                    </div>
                    <div class="flex items-center gap-3">
                        <Button
                            :label="$t('Edit order')"
                            icon="pi pi-pencil"
                            size="small"
                            outlined
                            @click="goToEditOrder"
                        />
                        <Button
                            :label="$t('Back to orders')"
                            icon="pi pi-arrow-left"
                            severity="secondary"
                            @click="() => {
                                router.visit(
                                    proxy.route ? proxy.route('orders.index') : '/admin/orders',
                                    {
                                        onStart: () => showLoading($t('Loading...')),
                                        onFinish: () => hideLoading()
                                    }
                                );
                            }"
                        />
                    </div>
                </div>
            </template>

            <template #content>
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                                <i class="pi pi-list text-white text-xl"></i>
                            </div>
                            <div>
                                <p class="text-sm text-surface-600">{{ $t('Total Items') }}</p>
                                <p class="text-2xl font-bold text-surface-900">{{ order.order_items?.length || 0 }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                                <i class="pi pi-dollar text-white text-xl"></i>
                            </div>
                            <div>
                                <p class="text-sm text-surface-600">{{ $t('Total Amount') }}</p>
                                <p class="text-2xl font-bold text-surface-900">{{ formatCurrency(calculateTotal(), getOrderCurrency()) }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full" :class="isPaymentConfirmed ? 'bg-green-500' : 'bg-amber-500'">
                                <i class="text-white text-xl flex items-center justify-center h-full" :class="isPaymentConfirmed ? 'pi pi-check' : 'pi pi-clock'"></i>
                            </div>
                            <div>
                                <p class="text-sm text-surface-600">{{ $t('Payment Status') }}</p>
                                <p class="text-2xl font-bold text-surface-900">{{ isPaymentConfirmed ? $t('Confirmed') : $t('Pending') }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full" :class="allConfirmed ? 'bg-green-500' : 'bg-amber-500'">
                                <i class="text-white text-xl flex items-center justify-center h-full" :class="allConfirmed ? 'pi pi-check' : 'pi pi-clock'"></i>
                            </div>
                            <div>
                                <p class="text-sm text-surface-600">{{ $t('Content Status') }}</p>
                                <p class="text-2xl font-bold text-surface-900">{{ allConfirmed ? $t('All Confirmed') : $t('Pending') }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Card>

        <!-- Client Information Card -->
        <Card class="mb-6">
            <template #header>
                <div class="p-6 pb-0">
                    <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <i class="pi pi-user text-primary-700 text-lg"></i>
                        </div>
                        <div>
                            <h2 class="text-xl font-bold text-surface-900">{{ $t('Client Information') }}</h2>
                            <p class="text-sm text-surface-600">{{ order.client?.email || '-' }}</p>
                        </div>
                    </div>
                </div>
            </template>
            <template #content>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                        <label class="block text-sm font-medium text-surface-700 mb-1">{{ $t('First Name') }}</label>
                        <p class="text-surface-900 font-medium">{{ order.client?.first_name || '-' }}</p>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-surface-700 mb-1">{{ $t('Last Name') }}</label>
                        <p class="text-surface-900 font-medium">{{ order.client?.last_name || '-' }}</p>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-surface-700 mb-1">{{ $t('Birthday') }}</label>
                        <p class="text-surface-900 font-medium">{{ formatDate(order.client?.birthday) }}</p>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-surface-700 mb-1">{{ $t('Phone') }}</label>
                        <p class="text-surface-900 font-medium">{{ order.client?.phone || '-' }}</p>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-surface-700 mb-1">{{ $t('Language') }}</label>
                        <p class="text-surface-900 font-medium">{{ getLanguageName(order.client?.locale) }}</p>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-surface-700 mb-1">{{ $t('Client ID') }}</label>
                        <p class="text-surface-900 font-medium">#{{ order.client?.id || '-' }}</p>
                    </div>
                </div>
            </template>
        </Card>

        <!-- Order Summary Card -->
        <Card class="mb-6">
            <template #header>
                <div class="p-6 pb-0">
                    <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                            <i class="pi pi-shopping-cart text-purple-700 text-lg"></i>
                        </div>
                        <div>
                            <h2 class="text-xl font-bold text-surface-900">{{ $t('Order Summary') }}</h2>
                            <p class="text-sm text-surface-600">{{ order.order_items?.length || 0 }} {{ $t('items') }} - {{ formatDate(order.created_at) }}</p>
                        </div>
                    </div>
                </div>
            </template>
            <template #content>
                <div class="space-y-4">
                    <!-- Order Details -->
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-1">{{ $t('Order Number') }}</label>
                            <p class="text-surface-900 font-semibold">#{{ order.number }}</p>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-1">{{ $t('Status') }}</label>
                            <Tag :value="getOrderStatusLabel(order.status)" :severity="getStatusSeverity(order.status)" v-tooltip.top="getCancellationTooltip(order, $t)" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-1">{{ $t('Created At') }}</label>
                            <p class="text-surface-900 font-medium">{{ formatDate(order.created_at) }}</p>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-1">{{ $t('Total Items') }}</label>
                            <p class="text-surface-900 font-semibold">{{ order.order_items?.length || 0 }}</p>
                        </div>
                    </div>

                    <!-- Items Table -->
                    <DataTable v-if="order.order_items && order.order_items.length > 0" :value="order.order_items" class="mt-4 payment-confirm-table" scrollable scrollHeight="600px" responsiveLayout="scroll">
                        <Column :header="$t('Site')" style="min-width:280px; width:30%" headerStyle="min-width:280px; width:30%">
                            <template #body="slotProps">
                                <span class="break-all">{{ slotProps.data.site?.url || slotProps.data.site?.name || '-' }}</span>
                            </template>
                        </Column>
                        <Column :header="$t('Type')" style="min-width:120px; white-space:nowrap">
                            <template #body="slotProps">
                                {{ slotProps.data.type?.name || '-' }}
                            </template>
                        </Column>
                        <Column :header="$t('Sale Value')" style="min-width:120px; white-space:nowrap">
                            <template #body="slotProps">
                                {{ formatCurrency(Math.ceil((slotProps.data.sale || 0) / 5) * 5, slotProps.data.sale_coin) }}
                            </template>
                        </Column>
                        <Column :header="$t('Currency')" style="min-width:120px; white-space:nowrap">
                            <template #body="slotProps">
                                {{ slotProps.data.sale_coin || '-' }}
                            </template>
                        </Column>
                        <Column :header="$t('Observations')" style="min-width:200px">
                            <template #body="slotProps">
                                {{ slotProps.data.observations || '-' }}
                            </template>
                        </Column>
                        <Column :header="$t('Responsible')" style="min-width:200px">
                            <template #body="slotProps">
                                <div v-if="getPublicationRecipient(slotProps.data)" class="flex flex-col">
                                    <span class="font-medium text-surface-900">
                                        {{ getPublicationRecipient(slotProps.data).name }}
                                    </span>
                                    <span class="text-xs text-surface-500 capitalize">
                                        {{ getPublicationRecipient(slotProps.data).type }}
                                    </span>
                                </div>
                                <span v-else class="text-surface-400">—</span>
                            </template>
                        </Column>
                        <Column :header="$t('Seller')" style="min-width:200px">
                            <template #body="slotProps">
                                <div v-if="getPublicationRecipient(slotProps.data)" class="flex flex-col gap-0.5">
                                    <span class="font-medium text-surface-900">{{ getPublicationRecipient(slotProps.data).seller_name || '-' }}</span>
                                    <span class="text-xs text-surface-500">{{ getPublicationRecipient(slotProps.data).seller_email || '-' }}</span>
                                    <span v-if="getPublicationRecipient(slotProps.data).seller_phone" class="text-xs text-surface-400">{{ getPublicationRecipient(slotProps.data).seller_phone }}</span>
                                    <Tag v-if="getPublicationRecipient(slotProps.data).email_target === 'seller'" :value="$t('Receive the email')" severity="warn" icon="pi pi-envelope" class="mt-1 text-xs w-fit" />
                                </div>
                                <span v-else class="text-surface-400">—</span>
                            </template>
                        </Column>
                        <Column :header="$t('Owner')" style="min-width:200px">
                            <template #body="slotProps">
                                <div v-if="getPublicationRecipient(slotProps.data)" class="flex flex-col gap-0.5">
                                    <span class="font-medium text-surface-900">{{ getPublicationRecipient(slotProps.data).owner_name || '-' }}</span>
                                    <span class="text-xs text-surface-500">{{ getPublicationRecipient(slotProps.data).owner_email || '-' }}</span>
                                    <span v-if="getPublicationRecipient(slotProps.data).owner_phone" class="text-xs text-surface-400">{{ getPublicationRecipient(slotProps.data).owner_phone }}</span>
                                    <Tag v-if="getPublicationRecipient(slotProps.data).email_target === 'owner'" :value="$t('Receive the email')" severity="info" icon="pi pi-envelope" class="mt-1 text-xs w-fit" />
                                </div>
                                <span v-else class="text-surface-400">—</span>
                            </template>
                        </Column>
                        <Column :header="$t('Actions')" style="min-width:120px; white-space:nowrap">
                            <template #body="slotProps">
                                <Tag v-if="slotProps.data.canceled_at" :value="$t('Canceled')" severity="danger" />
                            </template>
                        </Column>
                    </DataTable>

                    <!-- Seller & Owner blocks (only when email is missing) -->
                    <template v-if="hasMissingRecipientEmail">
                        <div v-for="item in order.order_items" :key="'recipient-' + item.id" class="mt-4">
                            <RecipientContactBlock
                                v-if="getPublicationRecipient(item) && !getPublicationRecipient(item).type"
                                :recipient="getPublicationRecipient(item)"
                                @updated="() => router.reload({ preserveScroll: true })"
                            />
                        </div>
                    </template>

                    <!-- Total -->
                    <div class="flex justify-end pt-4 border-t border-surface-200">
                        <div class="text-right space-y-1">
                            <!-- Subtotal -->
                            <div class="text-sm text-surface-600">
                                <span class="font-medium">{{ $t('Subtotal') }}: </span>
                                <span class="font-semibold">
                                    {{ formatCurrency(calculateSubtotal(), getOrderCurrency()) }}
                                </span>
                            </div>

                            <!-- Payment Tax (if exists) -->
                            <div v-if="calculateTotalTax() > 0" class="text-sm text-surface-600">
                                <span class="font-medium">{{ $t('Payment Processing Tax') }}: </span>
                                <span class="font-semibold text-orange-600">
                                    {{ formatCurrency(calculateTotalTax(), getOrderCurrency()) }}
                                </span>
                            </div>

                            <!-- Total -->
                            <div class="pt-1 border-t border-surface-200">
                                <p class="text-sm text-surface-600 mb-1">{{ $t('Total Amount') }}</p>
                                <p class="text-2xl font-bold text-surface-900">
                                    {{ formatCurrency(calculateTotal(), getOrderCurrency()) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Card>

        <!-- SECTION 0: Generate Invoice (Optional) -->
        <Card class="mb-6" :class="{ 'opacity-50 pointer-events-none': hasMissingRecipientEmail }">
            <template #header>
                <div class="p-6 pb-0">
                    <div class="flex items-center gap-3">
                        <div>
                            <h2 class="text-xl font-bold text-surface-900">
                                {{ $t('Generate Invoice (Optional)') }}
                            </h2>
                        </div>
                    </div>
                </div>
            </template>
            <template #content>
                <div class="space-y-4">
                    <!-- Existing Invoice Display -->
                    <div v-if="hasInvoice" class="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div class="flex items-start gap-3">
                            <i class="pi pi-check-circle text-green-600 text-xl mt-1"></i>
                            <div class="flex-1">
                                <p class="font-medium text-green-800 mb-2">
                                    {{ $t('Invoice has been generated for this order') }}
                                </p>
                                <div class="text-sm text-green-700 space-y-1">
                                    <div v-if="order.invoice?.number">
                                        <strong>{{ $t('Invoice Number') }}:</strong> #{{ order.invoice.number }}
                                    </div>
                                    <div v-if="order.invoice?.created_at">
                                        <strong>{{ $t('Generated Date') }}:</strong> {{ formatDate(order.invoice.created_at) }}
                                    </div>
                                    <div v-if="order.invoice?.emitted_at">
                                        <strong>{{ $t('Sent Date') }}:</strong> {{ formatDate(order.invoice.emitted_at) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Generate Invoice Section -->
                    <div v-else-if="canGenerateInvoice" class="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <div class="flex items-start gap-3">
                            <i class="pi pi-info-circle text-purple-600 text-xl mt-1"></i>
                            <div>
                                <p class="font-medium text-purple-800">{{ $t('Generate Invoice for Client') }}</p>
                                <p class="text-sm text-purple-600">
                                    {{ $t('Some clients need an invoice before making payment. Generate the invoice first to provide payment details to the client.') }}
                                </p>
                            </div>
                        </div>
                        <Button
                            :label="$t('Generate Invoice')"
                            icon="pi pi-file-pdf"
                            severity="help"
                            @click="openGenerateInvoiceDialog"
                            :disabled="invoiceForm.processing"
                        />
                    </div>

                    <!-- Invoice Generation Blocked -->
                    <div v-else class="p-4 bg-orange-50 rounded-lg border border-orange-200">
                        <div class="flex items-center gap-3">
                            <i class="pi pi-exclamation-triangle text-orange-600 text-xl"></i>
                            <div>
                                <p class="font-medium text-orange-800">{{ $t('Invoice Generation Blocked') }}</p>
                                <p class="text-sm text-orange-600">
                                    {{ $t('This action is no longer available for this order.') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Card>

        <!-- SECTION 1: Payment Link Request (Optional) -->
        <Card class="mb-6" :class="{ 'opacity-50 pointer-events-none': hasMissingRecipientEmail }">
            <template #header>
                <div class="p-6 pb-0">
                    <div class="flex items-center gap-3">
                        <div>
                            <h2 class="text-xl font-bold text-surface-900">
                                {{ $t('Payment Link Request (Optional)') }}
                            </h2>
                        </div>
                    </div>
                </div>
            </template>
            <template #content>
                <div class="space-y-4">
                    <!-- Existing Payment Request Display -->
                    <div v-if="paymentRequest" class="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div class="flex items-start gap-3">
                            <i class="pi pi-check-circle text-green-600 text-xl mt-1"></i>
                            <div class="flex-1">
                                <p class="font-medium text-green-800 mb-2">
                                    {{ $t('Payment request has been sent to client') }}
                                </p>
                                <div class="text-sm text-green-700 space-y-1">
                                    <div v-if="paymentRequest.sent_at">
                                        <strong>{{ $t('Sent Date') }}:</strong> {{ formatDate(paymentRequest.sent_at) }}
                                    </div>
                                    <div v-if="paymentRequest.payment_link">
                                        <strong>{{ $t('Payment Link') }}:</strong>
                                        <a :href="paymentRequest.payment_link" target="_blank" class="text-blue-600 hover:underline ml-1 break-all">
                                            {{ paymentRequest.payment_link }}
                                            <i class="pi pi-external-link ml-1 text-xs"></i>
                                        </a>
                                    </div>
                                    <div v-if="paymentRequest.bank_account?.name">
                                        <strong>{{ $t('Bank Account') }}:</strong> {{ paymentRequest.bank_account.name }}
                                    </div>
                                    <div v-if="paymentRequest.company?.name">
                                        <strong>{{ $t('Company') }}:</strong> {{ paymentRequest.company.name }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Payment Link Request Blocked -->
                    <div v-else-if="props.order.payment_date || props.order.invoice || props.order.deferred_payment || (!paymentRequest && isPaymentLinkAlreadySent)" class="p-4 bg-orange-50 rounded-lg border border-orange-200">
                        <div class="flex items-start gap-3">
                            <i class="pi pi-exclamation-triangle text-orange-600 text-xl mt-1"></i>
                            <div>
                                <p class="font-medium text-orange-800">{{ $t('Payment Request Disabled') }}</p>
                                <p class="text-sm text-orange-600">
                                    {{ $t('This action is no longer available for this order.') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Send Payment Link Button -->
                    <div v-else-if="!isPaymentLinkAlreadySent" class="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div class="flex items-start gap-3">
                            <i class="pi pi-info-circle text-blue-600 text-xl mt-1"></i>
                            <div>
                                <p class="font-medium text-blue-800">{{ $t('Send Payment Link to Client') }}</p>
                                <p class="text-sm text-blue-600">
                                    {{ $t('Optionally send a payment link to the client before confirming payment.') }}
                                </p>
                            </div>
                        </div>
                        <Button
                            :label="$t('Send Payment Link')"
                            icon="pi pi-link"
                            severity="info"
                            @click="openPaymentLinkDialog"
                            :disabled="paymentRequestForm.processing"
                        />
                    </div>
                </div>
            </template>
        </Card>

        <!-- SECTION 2: Payment Confirmation -->
        <Card class="mb-6" :class="{ 'opacity-50 pointer-events-none': hasMissingRecipientEmail }">
            <template #header>
                <div class="p-6 pb-0">
                    <div class="flex items-center gap-3">
                        <div>
                            <h2 class="text-xl font-bold text-surface-900">
                                {{ $t('Payment Confirmation') }}
                            </h2>
                            <p class="text-sm font-medium" :class="isPaymentConfirmed ? 'text-green-600' : 'text-amber-600'">
                                {{ isPaymentConfirmed ? $t('Payment confirmed') : $t('Awaiting payment confirmation') }}
                            </p>
                        </div>
                    </div>
                </div>
            </template>
            <template #content>
                <div class="space-y-4">
                    <!-- Payment Status Message -->
                    <div v-if="isPaymentConfirmed && isPaymentConfirmed.type === 'success'" class="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div class="flex items-center gap-2 text-green-700">
                            <i class="pi pi-check-circle text-xl"></i>
                            <div>
                                <p class="font-medium">
                                    {{ props.order.deferred_payment
                                        ? $t('Deferred payment has been confirmed for this order')
                                        : $t('Payment has already been confirmed for this order')
                                    }}
                                </p>
                                <p v-if="props.order.payment_date" class="text-sm text-green-600 mt-1">
                                    {{ $t('Payment Date') }}: {{ formatDate(props.order.payment_date) }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="isPaymentConfirmed && isPaymentConfirmed.type === 'warn'" class="p-4 bg-red-50 rounded-lg border border-red-200">
                        <div class="flex items-center gap-2 text-red-700">
                            <i class="pi pi-exclamation-triangle text-xl"></i>
                            <p class="font-medium">{{ $t('This order did not have its payment information provided, but it has already been authorized.') }}</p>
                        </div>
                    </div>

                    <!-- Payment Method Selection -->
                    <div v-if="!isPaymentConfirmed" class="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div class="flex items-start gap-3">
                            <Checkbox
                                v-model="confirmForm.deferred_payment"
                                inputId="deferred_payment"
                                :binary="true"
                            />
                            <label for="deferred_payment" class="cursor-pointer">
                                <div class="font-medium text-surface-900">{{ $t('Deferred Payment') }}</div>
                                <div class="text-sm text-surface-600">{{ $t('When this option is selected, payment will be processed at the end of the purchase.') }}</div>
                            </label>
                        </div>
                    </div>

                    <!-- Payment Date Input -->
                    <div v-if="!confirmForm.deferred_payment && !isPaymentConfirmed" class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <label class="block text-sm font-medium text-surface-700 mb-3">
                            {{ $t('Select the Payment Date') }}
                            <span class="text-red-500">*</span>
                        </label>
                        <Calendar
                            v-model="confirmForm.payment_date"
                            showIcon
                            dateFormat="dd/mm/yy"
                            class="w-full md:w-[400px]"
                            :class="{ 'p-invalid': confirmForm.errors.payment_date }"
                        />
                        <small v-if="confirmForm.errors.payment_date" class="text-red-600 block mt-2">
                            {{ confirmForm.errors.payment_date }}
                        </small>
                    </div>

                    <!-- Action Buttons -->
                    <div v-if="!isPaymentConfirmed" class="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-start gap-3">
                            <i class="pi pi-info-circle text-primary-500 text-xl mt-1"></i>
                            <div>
                                <p class="text-surface-900 font-medium">{{ $t('Ready to proceed?') }}</p>
                                <p class="text-sm text-surface-600">{{ $t('Confirm the payment or cancel the order if necessary') }}</p>
                            </div>
                        </div>
                        <div class="flex gap-3">
                            <Button
                                :label="$t('Cancel Order')"
                                icon="pi pi-times"
                                severity="danger"
                                outlined
                                @click="showCancelDialog"
                                :loading="cancelForm.processing"
                                :disabled="!canCancelOrder(props.order) || cancelForm.processing || confirmForm.processing"
                            />
                            <Button
                                :label="$t('Confirm Payment')"
                                icon="pi pi-check"
                                severity="success"
                                @click="confirmPayment"
                                :loading="confirmForm.processing"
                                :disabled="!canConfirmPayment || confirmForm.processing || cancelForm.processing"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </Card>

        <!-- SECTION 3: Content Review Component -->
        <ContentReviewSection
            class="mb-6"
            :order="order"
            :items-with-content="itemsWithContent"
            :all-confirmed="allConfirmed"
            :has-content="hasContent"
            :is-enabled="isContentReviewEnabled"
            @content-confirmed="handleRefreshNeeded"
            @refresh-needed="handleRefreshNeeded"
        />

        <!-- SECTION 4: Publication Component -->
        <PublicationSection
            class="mb-6"
            :order="order"
            :items-with-content="itemsWithContent"
            :all-sent="allSent"
            :has-confirmed-content="hasConfirmedContent"
            :is-enabled="isPublicationEnabled"
            :step-number="hasAutoContent ? 4 : 5"
            @publication-confirmed="handlePublicationConfirmed"
            @refresh-needed="handleRefreshNeeded"
        />

        <!-- Payment Link Request Dialog -->
        <Dialog
            v-model:visible="showPaymentLinkDialog"
            :style="{ width: '90vw', maxWidth: '800px' }"
            :header="$t('Send Payment Link to Client')"
            :modal="true"
            :closable="!paymentRequestForm.processing"
        >
            <div class="space-y-6">
                <!-- Info Message -->
                <div class="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div class="flex items-start gap-3">
                        <i class="pi pi-info-circle text-blue-600 text-xl mt-1"></i>
                        <div>
                            <p class="font-medium text-blue-800 mb-1">{{ $t('Payment Request Information') }}</p>
                            <p class="text-sm text-blue-600">
                                {{ $t('This will send an email to the client with payment information. The same bank account and company information will be pre-selected when generating the invoice later.') }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Company Selection -->
                <CompanySelectionSection
                    v-if="companies.length > 0"
                    :companies="companies"
                    :selected-company="selectedCompany"
                    :is-create-mode="true"
                    @update:selectedCompany="(company) => {
                        selectedCompany = company;
                        paymentRequestForm.company_profile_id = company?.id || null;
                    }"
                    @create-new="createNewCompany"
                    @edit-company="openCompanyModal"
                />

                <!-- Bank Account Selection -->
                <BankAccountSelectionSection
                    v-if="bankAccounts.length > 0"
                    :bank-accounts="bankAccounts"
                    :selected-bank-account="selectedBankAccount"
                    :selected-company="selectedCompany"
                    :is-create-mode="true"
                    @update:selectedBankAccount="(bankAccount) => {
                        selectedBankAccount = bankAccount;
                        paymentRequestForm.bank_account_id = bankAccount?.id || null;

                        if (bankAccount?.payment_link) {
                            paymentLinkOption.value = 'bank_account';
                            customPaymentLink.value = '';
                        } else if (paymentLinkOption.value === 'bank_account') {
                            paymentLinkOption.value = 'none';
                            customPaymentLink.value = '';
                        }
                    }"
                    @create-new="createNewBankAccount"
                    @edit-bank-account="openBankAccountModal"
                />

                <!-- Payment Information Component -->
                <PaymentInformationSection
                    ref="paymentLinkComponentRef"
                    :selected-bank-account="selectedBankAccount"
                    :invoice-data="{ bank: selectedBankAccount?.name || 'WISE' }"
                    :is-edit-mode="true"
                    user-type="admin"
                    :is-invoice-mode="false"
                    :payment-link-value="finalPaymentLink"
                    :show-no-payment-link-option="false"
                    @update:paymentLinkValue="handlePaymentLinkValueUpdate"
                    @paymentLinkOption:update="handlePaymentLinkOptionUpdate"
                    @customPaymentLink:update="handleCustomPaymentLinkUpdate"
                />

                <!-- Form Errors -->
                <div v-if="paymentRequestForm.errors.payment_link" class="p-4 bg-red-50 rounded-lg border border-red-200">
                    <div class="flex items-center gap-2 text-red-700">
                        <i class="pi pi-exclamation-circle"></i>
                        <small class="font-medium">{{ paymentRequestForm.errors.payment_link }}</small>
                    </div>
                </div>
            </div>

            <template #footer>
                <Button
                    :label="$t('Cancel')"
                    severity="secondary"
                    @click="closePaymentLinkDialog"
                    :disabled="paymentRequestForm.processing"
                />
                <Button
                    :label="$t('Send to Client')"
                    icon="pi pi-send"
                    @click="sendPaymentLinkRequest"
                    :loading="paymentRequestForm.processing"
                    :disabled="!canSendPaymentRequest"
                />
            </template>
        </Dialog>

        <!-- Cancel Order Confirmation Dialog -->
        <Dialog
            v-model:visible="deleteOrderDialog"
            :style="{ width: '450px' }"
            :header="$t('Confirm Cancel')"
            :modal="true"
        >
            <div class="flex items-center gap-4">
                <i class="pi pi-exclamation-triangle text-3xl text-orange-500" />
                <span>
                    {{ $t('Are you sure you want to cancel this order') }} <b>#{{ order.number }}</b>?
                </span>
            </div>
            <div class="mt-4">
                <label for="paymentCancelReason" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Cancellation Reason (Optional)') }}
                </label>
                <Textarea
                    id="paymentCancelReason"
                    v-model="cancelReason"
                    rows="3"
                    autoResize
                    class="w-full"
                    maxlength="1000"
                    :placeholder="$t('Describe the reason for cancellation...')"
                />
            </div>
            <template #footer>
                <Button
                    :label="$t('No')"
                    icon="pi pi-times"
                    text
                    @click="deleteOrderDialog = false"
                    :disabled="cancelForm.processing"
                />
                <Button
                    :label="$t('Yes')"
                    icon="pi pi-ban"
                    severity="danger"
                    @click="cancelOrder"
                    :loading="cancelForm.processing"
                    :disabled="cancelForm.processing"
                />
            </template>
        </Dialog>

        <!-- Company Modal -->
        <CompanyFormModal
            v-model="showCompanyModal"
            :company="editingCompany"
            @saved="handleCompanySaved"
        />

        <!-- Bank Account Modal -->
        <BankAccountFormModal
            v-model="showBankAccountModal"
            :bank-account="editingBankAccount"
            :companies="companies"
            @saved="handleBankAccountSaved"
        />

        <!-- Generate Invoice Modal -->
        <Dialog
            v-model:visible="showGenerateInvoiceDialog"
            :style="{ width: '95vw', maxWidth: '1200px', height: '90vh' }"
            :header="$t('Generate Invoice')"
            :modal="true"
            :closable="!invoiceForm.processing"
            :maximizable="true"
            class="invoice-modal"
        >
            <div class="h-full overflow-y-auto">
                <!-- Invoice Form Header -->
                <div class="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div class="flex items-start gap-3">
                        <i class="pi pi-info-circle text-blue-600 text-xl mt-1"></i>
                        <div class="flex-1">
                            <h3 class="font-medium text-blue-800 mb-2">{{ $t('Creating Invoice for Order') }} #{{ order.number }}</h3>
                            <p class="text-sm text-blue-600">
                                {{ $t('Fill in the invoice details below. You can choose to send it to the client immediately or save as draft.') }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Invoice View Component -->
                <InvoiceView
                    :order="order"
                    :invoice="null"
                    :companies="companies"
                    :bankAccounts="bankAccounts"
                    :invoiceSettings="invoiceSettings"
                    :userType="'admin'"
                    :isCreateMode="true"
                    :paymentRequest="currentPaymentRequestData"
                    :publicationRecipients="publicationRecipientsMap"
                    @close="closeGenerateInvoiceDialog"
                />
            </div>
        </Dialog>

        <!-- Payment Request Mismatch Modal -->
        <PaymentRequestMismatchModal
            v-model:visible="showPaymentMismatchDialog"
            :payment-request="paymentRequest || (invoice ? {
                company_profile_id: invoice.company_profile_id,
                bank_account_id: invoice.bank_account_id,
                payment_link: invoice.payment_link,
                company: companies.find(c => c.id === invoice.company_profile_id),
                bank_account: bankAccounts.find(b => b.id === invoice.bank_account_id),
                sent_at: invoice.created_at
            } : null)"
            :order="order"
            :selected-company="selectedCompany"
            :selected-bank-account="selectedBankAccount"
            :final-payment-link="finalPaymentLink"
            @proceed="proceedWithPaymentMismatch"
            @cancel="cancelPaymentMismatch"
        />
    </div>
</template>

<style scoped>
/* Payment confirm items table: header titles must always stay on a single line */
.payment-confirm-table :deep(.p-datatable-column-title) {
    white-space: nowrap;
}

.payment-confirm-table :deep(.p-datatable-header-cell) {
    white-space: nowrap;
}

.invoice-modal .p-dialog-content {
    padding: 0;
    height: calc(90vh - 150px);
    overflow: hidden;
}

.invoice-modal .p-dialog-content > div {
    padding: 1.5rem;
    height: 100%;
    overflow-y: auto;
}
</style>
